import { useEffect, useState, useCallback } from "react";
import { Boxes, AlertTriangle, Save, TrendingUp, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminInventory() {
  const [data, setData] = useState(null);
  const [anom, setAnom] = useState(null);
  const [threshold, setThreshold] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/marketplace/admin/inventory").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
    api.get("/marketplace/admin/inventory/anomalies").then((r) => setAnom(r.data)).catch(() => {});
    api.get("/marketplace/admin/inventory/settings").then((r) => setThreshold(String(r.data.default_low_stock_threshold))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try { await api.put("/marketplace/admin/inventory/settings", { default_low_stock_threshold: parseInt(threshold, 10) }); toast.success("Default threshold saved"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const t = data?.totals || {};
  const recon = anom?.reconciliation || {};

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Inventory Monitoring" icon={Boxes}
        subtitle="Marketplace-wide inventory health. Monitoring only — administrative changes to seller stock require an authorized, audited workflow."
        actions={<button onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600" data-testid="admin-inv-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>} />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="admin-inv-totals">
        {[["Products", t.products], ["Total units", t.total_units], ["Reserved", t.reserved_units], ["Low stock", t.low_stock], ["Out of stock", t.out_of_stock], ["Inventory value", t.inventory_value != null ? `₹${t.inventory_value.toLocaleString()}` : "—"]].map(([l, v]) => (
          <div key={l} className="card-soft p-4"><p className="text-xs text-slate-500">{l}</p><p className="text-xl font-bold text-slate-800">{v ?? 0}</p></div>
        ))}
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <div className="card-soft p-5" data-testid="admin-inv-reconciliation">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><AlertTriangle className="h-4 w-4 text-amber-500" /> Reconciliation exceptions</p>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Stuck reservations (past expiry &gt; 1h)</span><span className={recon.stuck_reservations ? "font-bold text-rose-600" : "text-slate-400"}>{recon.stuck_reservations ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Products with negative reserved</span><span className={recon.negative_reserved_products ? "font-bold text-rose-600" : "text-slate-400"}>{recon.negative_reserved_products ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Active reservations</span><span className="text-slate-500">{t.active_reservations ?? 0}</span></div>
          </div>
        </div>
        <div className="card-soft p-5">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><TrendingUp className="h-4 w-4 text-blue-500" /> Anomaly flags <span className="text-[10px] font-normal text-slate-400">(detection only)</span></p>
          {(anom?.big_swings?.length || anom?.repeated_manual?.length) ? (
            <div className="space-y-1.5 text-sm">
              {anom.repeated_manual.map((r) => <div key={r.product_id} className="flex justify-between"><span className="truncate text-slate-600">Repeated manual edits: {r.product_id}</span><span className="font-semibold text-amber-600">{r.manual_adjustments}×</span></div>)}
              {anom.big_swings.slice(0, 8).map((r, i) => <div key={i} className="flex justify-between"><span className="truncate text-slate-600">Large swing: {r.product_id}</span><span className={`font-semibold ${r.change > 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.change > 0 ? "+" : ""}{r.change}</span></div>)}
            </div>
          ) : <p className="text-sm text-slate-400">No inventory anomalies detected.</p>}
        </div>
      </div>

      <div className="mb-4 card-soft p-5">
        <p className="mb-1 text-sm font-semibold text-slate-800">Marketplace default low-stock threshold</p>
        <p className="mb-2 text-xs text-slate-500">Applied to products where the seller hasn't set their own threshold.</p>
        <div className="flex items-center gap-2"><Input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-32" data-testid="admin-inv-threshold" />
          <button onClick={saveSettings} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="admin-inv-threshold-save"><Save className="h-4 w-4" /> Save</button></div>
      </div>

      <div className="card-soft overflow-x-auto p-0" data-testid="admin-inv-sellers">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400"><tr><th className="p-3">Seller</th><th className="p-3 text-center">Products</th><th className="p-3 text-center">Units</th><th className="p-3 text-center">Low</th><th className="p-3 text-center">Out</th></tr></thead>
          <tbody>
            {(data?.sellers || []).map((s) => (
              <tr key={s.seller_id} className="border-t border-slate-100"><td className="p-3 font-mono text-xs text-slate-600">{s.seller_id.slice(0, 12)}…</td><td className="p-3 text-center">{s.products}</td><td className="p-3 text-center font-semibold">{s.units}</td><td className="p-3 text-center text-amber-600">{s.low}</td><td className="p-3 text-center text-rose-600">{s.out}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
